import {
    Avatar,
    Box,
    Button,
    Card,
    Center,
    Flex,
    HStack,
    List,
    ListItem,
    Spinner,
    Tag,
    TagRightIcon,
    Text,
    useColorModeValue,
    useDisclosure,
    useTheme,
    VStack,
} from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { User } from '../../types';
import { useGetTeamAccountAssignedEmployees } from '../../query/resource-hooks/teamAccountEmployee';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { AssignEmployeesToTeamAccountDrawer } from './AssignEmployeesToTeamAccountDrawer';
import { FiUsers } from 'react-icons/fi';

const AssignedEmployeeListItem: React.FC<{ user: User; onClick?: () => void }> = ({ user, onClick }) => {
    const intl = useIntl();
    const theme = useTheme();
    const hoverBg = useColorModeValue(theme.colors.gray[200], theme.colors.gray[600]);

    return (
        <ListItem m={1} role="group" cursor="pointer" borderRadius={100} onClick={onClick}>
            <Card
                p={2}
                borderRadius={'lg'}
                _hover={{
                    bg: hoverBg,
                    boxShadow: 'box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px',
                }}
            >
                <HStack justifyContent="space-between" width="100%">
                    <HStack>
                        <Avatar boxSize={10} name={user.surname || user.firstName} />
                        <VStack align={'start'} spacing={0}>
                            <Text fontWeight={'600'}>{`${user?.surname || ''} ${user.firstName}`}</Text>
                            <Text fontSize={'small'}>{user.role ? intl.formatMessage({ id: user.role }) : '-'}</Text>
                        </VStack>
                    </HStack>
                    <ChevronRightIcon boxSize={5} mr={2} display={{ base: 'none', md: 'block' }} />
                </HStack>
            </Card>
        </ListItem>
    );
};

interface TeamAccountAssignedEmployeesListProps {
    teamAccountId: number;
}

export const TeamAccountAssignedEmployeesList: React.FC<TeamAccountAssignedEmployeesListProps> = ({ teamAccountId }) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const assignDrawer = useDisclosure();
    const { data, isLoading } = useGetTeamAccountAssignedEmployees(teamAccountId);

    const employees = data?.data ?? [];

    return (
        <>
            <Flex justify={'center'}>
                <Flex flexDir={'column'} maxW={1600} flex={1}>
                    <Flex justify={'space-between'}>
                        <Button size="sm" alignSelf={'flex-start'} mb={3} onClick={assignDrawer.onOpen}>
                            {intl.formatMessage({ id: 'teamAccounts.assignEmployees.title' })}
                        </Button>
                        <Box>
                            <Tag>
                                {employees.length ?? 0}
                                <TagRightIcon as={FiUsers} />
                            </Tag>
                        </Box>
                    </Flex>

                    {isLoading ? (
                        <Center py={8}>
                            <Spinner />
                        </Center>
                    ) : employees.length === 0 ? (
                        <Center py={8}>
                            <Text color="gray.500">{intl.formatMessage({ id: 'noData' })}</Text>
                        </Center>
                    ) : (
                        <List>
                            {employees.map((user) => (
                                <AssignedEmployeeListItem
                                    key={user.id}
                                    user={user}
                                    onClick={() => navigate(`/employees/detail/${user.id}`)}
                                />
                            ))}
                        </List>
                    )}
                </Flex>
            </Flex>

            <AssignEmployeesToTeamAccountDrawer isOpen={assignDrawer.isOpen} onClose={assignDrawer.onClose} teamAccountId={teamAccountId} />
        </>
    );
};
